// SA-7 — форма слота: создание/редактирование, брак, передел, BOM (фаза 4)

import { useCallback, useEffect, useMemo, useState } from 'react'

import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  COL_INVENTORY_MOVEMENTS,
  COL_MACHINES,
  COL_MACHINE_EVENTS,
  COL_PRODUCT_COMPONENTS,
  COL_PRODUCTS,
  COL_PRODUCTION_REQUEST_LINES,
  COL_SCHEDULE_SLOTS,
  COL_SHIFTS,
  COL_TIMESHEET_ENTRIES,
} from '../../config/collections'
import { PERMISSION_KEYS } from '../../config/permissions'
import { Alert, FormField, Modal, ModalActions, modalBtn } from '../../components/ui'
import { formInputProps } from '../../components/ui/FormField'
import { loadCompatibleMachines } from '../../lib/productMachines'
import { formatMachineLabel } from '../../lib/machines'
import {
  eventsForMachineInRange,
  eventDurationHours,
  isEventOpen,
  MACHINE_EVENT_REASONS,
  machineEventReasonLabel,
} from '../../lib/machineEvents'
import { applyDowntimeToSchedule } from '../../lib/scheduleShiftOnDowntime'
import {
  calculatePlannedQuantity,
  addHoursToLocalDateTime,
  formatDateTimeRu,
  formatDurationHours,
  formatLocalDateTimeInput,
  formatQty,
  hoursForProductQuantity,
  localDateTimeToIso,
  plannedQuantityFieldLabel,
  plannedQuantityHint,
  productOutputUnitsMatch,
  productQuantityUnitLabel,
  resolveProductOutput,
  slotDurationHours,
  SCHEDULE_SLOT_STATUS_LABELS,
} from '../../lib/scheduleCalculations'
import { remainingToSchedule } from '../../lib/requestLineStatus'
import { collectScheduleWarnings } from '../../lib/scheduleConflicts'
import { fetchCalendarMap } from '../../lib/loadProductionCalendar'
import { resolveWorkingBrigade } from '../../lib/shiftRotation'
import { isRotationShift } from '../../lib/shifts'
import { afterSlotSaved, linkMaterialLotToSlot } from '../../lib/scheduleSlotLifecycle'
import { syncRequestLineStatus } from '../../lib/scheduleOperations'
import { slotCostBreakdown, type SlotCostBreakdown } from '../../lib/productionCostActual'
import { ScheduleOperationPanel } from '../../components/ScheduleOperationPanel'
import { pb } from '../../pocketbase'
import { useAuth } from '../../context/AuthContext'
import type {
  InventoryMovementRecord,
  MachineEventRecord,
  MachineEventReason,
  MachineRecord,
  ProductComponentRecord,
  ProductionRequestLineRecord,
  ProductionCalendarDayRecord,
  ProductRecord,
  ScheduleSlotRecord,
  ShiftRecord,
  TimesheetEntryRecord,
} from '../../types/records'

function formatMoney(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function hasBomChangedSinceSlot(allComponents: ProductComponentRecord[], slotPlacedAt: string): boolean {
  const placedDay = slotPlacedAt.slice(0, 10)
  return allComponents.some((c) => {
    const from = c.validFrom?.slice(0, 10)
    return Boolean(from && from > placedDay)
  })
}

function slotPlacedAt(slot: ScheduleSlotRecord): string {
  return slot.created || slot.plannedStart
}

export type ScheduleSlotFormModalProps = {
  slotId: string | null
  initialMachineId?: string
  initialStart?: string
  initialRequestLineId?: string
  onClose: () => void
  onSaved: () => void
}

function defaultEnd(startLocal: string, hours = 4): string {
  return addHoursToLocalDateTime(startLocal, hours)
}

function toLocalInput(iso: string): string {
  const d = new Date(iso)
  return formatLocalDateTimeInput(d)
}

type ScheduleMode = 'line' | 'duration' | 'quantity'

export function ScheduleSlotFormModal({
  slotId,
  initialMachineId,
  initialStart,
  initialRequestLineId,
  onClose,
  onSaved,
}: ScheduleSlotFormModalProps) {
  const { user, can } = useAuth()
  const canWrite = can(PERMISSION_KEYS.PRODUCTION_SCHEDULE_WRITE)
  const isEdit = Boolean(slotId)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [reworkBusy, setReworkBusy] = useState(false)
  const [err, setErr] = useState('')
  const [slot, setSlot] = useState<ScheduleSlotRecord | null>(null)
  const [lines, setLines] = useState<ProductionRequestLineRecord[]>([])
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [compatibleMachines, setCompatibleMachines] = useState<MachineRecord[]>([])
  const [shifts, setShifts] = useState<ShiftRecord[]>([])
  const [allSlots, setAllSlots] = useState<ScheduleSlotRecord[]>([])
  const [bomWarning, setBomWarning] = useState(false)
  const [machineEvents, setMachineEvents] = useState<MachineEventRecord[]>([])
  const [eventReason, setEventReason] = useState<MachineEventReason>('breakdown')
  const [eventNotes, setEventNotes] = useState('')
  const [eventStartLocal, setEventStartLocal] = useState(() => formatLocalDateTimeInput(new Date()))
  const [eventDurationMinutes, setEventDurationMinutes] = useState('60')
  const [eventSaving, setEventSaving] = useState(false)
  const [eventShiftMsg, setEventShiftMsg] = useState('')
  const [costBreakdown, setCostBreakdown] = useState<SlotCostBreakdown | null>(null)
  const [materialLotCode, setMaterialLotCode] = useState('')
  const [materialLotQty, setMaterialLotQty] = useState('')

  const [requestLineId, setRequestLineId] = useState('')
  const [machineId, setMachineId] = useState(initialMachineId ?? '')
  const [shiftId, setShiftId] = useState('')
  const [plannedStart, setPlannedStart] = useState(initialStart ? toLocalInput(initialStart) : '')
  const [plannedEnd, setPlannedEnd] = useState(initialStart ? toLocalInput(defaultEnd(initialStart)) : '')
  const [plannedQuantity, setPlannedQuantity] = useState('')
  const [actualQuantity, setActualQuantity] = useState('')
  const [defectQuantity, setDefectQuantity] = useState('')
  const [status, setStatus] = useState<ScheduleSlotRecord['status']>('planned')
  const [notes, setNotes] = useState('')
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('line')
  const [fetchedProduct, setFetchedProduct] = useState<ProductRecord | null>(null)
  const [calendarMap, setCalendarMap] = useState(
    () => new Map<string, Pick<ProductionCalendarDayRecord, 'isWorking' | 'workingBrigade'>>(),
  )

  const selectedLine = useMemo(
    () => lines.find((l) => l.id === requestLineId) ?? null,
    [lines, requestLineId],
  )

  const expandedProduct = selectedLine?.expand?.product ?? null

  useEffect(() => {
    let cancelled = false
    const productId = selectedLine?.product
    if (!productId) {
      setFetchedProduct(null)
      return
    }
    const hasOutputSpec =
      Boolean(expandedProduct?.outputUnit) ||
      (expandedProduct?.sheetsPerUnit != null && expandedProduct.sheetsPerUnit > 0)
    if (hasOutputSpec) {
      setFetchedProduct(null)
      return
    }
    void pb
      .collection(COL_PRODUCTS)
      .getOne<ProductRecord>(productId)
      .then((p) => {
        if (!cancelled) setFetchedProduct(p)
      })
      .catch(() => {
        if (!cancelled) setFetchedProduct(null)
      })
    return () => {
      cancelled = true
    }
  }, [selectedLine?.product, expandedProduct?.outputUnit, expandedProduct?.sheetsPerUnit])

  const selectedProduct = fetchedProduct ?? expandedProduct

  const selectedMachine = useMemo(() => {
    const fromAll = machines.find((m) => m.id === machineId)
    if (fromAll) return fromAll
    return compatibleMachines.find((m) => m.id === machineId) ?? null
  }, [machines, compatibleMachines, machineId])

  const machineOptions = useMemo(() => {
    if (isEdit) {
      return selectedMachine ? [selectedMachine] : []
    }
    return compatibleMachines
  }, [isEdit, selectedMachine, compatibleMachines])

  const lineRemaining = useMemo(() => {
    if (!selectedLine) return 0
    const lineSlots = allSlots.filter(
      (s) => s.requestLine === selectedLine.id && s.id !== slotId && s.status !== 'cancelled',
    )
    return remainingToSchedule(selectedLine, lineSlots)
  }, [selectedLine, allSlots, slotId])

  const warnings = useMemo(() => {
    if (!machineId || !plannedStart || !plannedEnd) return []
    const list = collectScheduleWarnings(
      {
        id: slotId ?? '',
        machine: machineId,
        plannedStart: localDateTimeToIso(plannedStart),
        plannedEnd: localDateTimeToIso(plannedEnd),
        status,
      },
      allSlots,
      selectedMachine,
      calendarMap,
    )
    const dateKey = plannedStart.slice(0, 10)
    const brigade = resolveWorkingBrigade(dateKey, calendarMap)
    const slotShift = shifts.find((s) => s.id === shiftId)
    if (
      brigade &&
      shiftId &&
      slotShift &&
      isRotationShift(slotShift) &&
      brigade !== shiftId
    ) {
      const brigadeName = shifts.find((s) => s.id === brigade)?.name ?? 'бригада из календаря'
      list.push({
        type: 'maintenance_warning',
        message: `Смена слота не совпадает с рабочей бригадой в календаре (${brigadeName}).`,
      })
    }
    return list
  }, [machineId, plannedStart, plannedEnd, status, slotId, allSlots, selectedMachine, calendarMap, shiftId, shifts])

  useEffect(() => {
    const key = plannedStart.slice(0, 10)
    if (key.length < 10) {
      setCalendarMap(new Map())
      return
    }
    let cancelled = false
    void fetchCalendarMap(key, key).then((map) => {
      if (!cancelled) setCalendarMap(map)
    })
    return () => {
      cancelled = true
    }
  }, [plannedStart])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setErr('')
      try {
        const [lineList, machineList, shiftList, slotList] = await Promise.all([
          pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
            filter: 'status = "pending" || status = "scheduled" || status = "in_progress"',
            expand: 'product,request',
            sort: 'plannedProduction,priority',
          }),
          pb.collection(COL_MACHINES).getFullList<MachineRecord>({ sort: 'type,sort,number' }),
          pb.collection(COL_SHIFTS).getFullList<ShiftRecord>({ sort: 'sort' }),
          pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
            filter: 'status != "cancelled"',
          }),
        ])
        if (cancelled) return
        setLines(lineList)
        setMachines(machineList)
        setShifts(shiftList.filter((s) => s.active))
        setAllSlots(slotList)

        if (slotId) {
          const row = await pb.collection(COL_SCHEDULE_SLOTS).getOne<ScheduleSlotRecord>(slotId, {
            expand: 'requestLine.product,machine,shift,reworkOf',
          })
          if (cancelled) return
          setSlot(row)
          setRequestLineId(row.requestLine)
          setMachineId(row.machine)
          setShiftId(row.shift ?? '')
          setPlannedStart(toLocalInput(row.plannedStart))
          setPlannedEnd(toLocalInput(row.plannedEnd))
          setPlannedQuantity(String(row.plannedQuantity))
          setActualQuantity(row.actualQuantity != null ? String(row.actualQuantity) : '')
          setDefectQuantity(row.defectQuantity != null ? String(row.defectQuantity) : '')
          setStatus(row.status)
          setNotes(row.notes ?? '')

          const productId = row.expand?.requestLine?.product
          if (productId) {
            const bom = await pb.collection(COL_PRODUCT_COMPONENTS).getFullList<ProductComponentRecord>({
              filter: `product="${productId}"`,
            })
            if (!cancelled) setBomWarning(hasBomChangedSinceSlot(bom, slotPlacedAt(row)))
          }
        } else {
          setSlot(null)
          setRequestLineId(initialRequestLineId ?? lineList[0]?.id ?? '')
          setMachineId(initialMachineId ?? '')
          setShiftId('')
          const start = initialStart ?? new Date().toISOString()
          setPlannedStart(toLocalInput(start))
          setPlannedEnd(toLocalInput(defaultEnd(start)))
          setPlannedQuantity('')
          setActualQuantity('')
          setDefectQuantity('')
          setStatus('planned')
          setNotes('')
          setBomWarning(false)
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slotId, initialMachineId, initialStart, initialRequestLineId])

  useEffect(() => {
    if (!slotId || !slot) {
      setCostBreakdown(null)
      return
    }
    let cancelled = false
    async function loadCost() {
      const productId = slot?.expand?.requestLine?.product
      if (!productId || typeof productId !== 'string') {
        if (!cancelled) setCostBreakdown(null)
        return
      }
      try {
        const [bom, movements, timesheets] = await Promise.all([
          pb.collection(COL_PRODUCT_COMPONENTS).getFullList<ProductComponentRecord>({
            filter: `product="${productId}"`,
            expand: 'consumable',
          }),
          pb.collection(COL_INVENTORY_MOVEMENTS).getFullList<InventoryMovementRecord>({
            filter: `referenceType="schedule_slot" && referenceId="${slotId}"`,
            expand: 'consumable',
          }),
          pb.collection(COL_TIMESHEET_ENTRIES).getFullList<TimesheetEntryRecord>({
            filter: `scheduleSlot="${slotId}"`,
            expand: 'worker',
          }),
        ])
        if (cancelled) return
        setCostBreakdown(
          slotCostBreakdown({
            bom,
            slotDateIso: slot!.plannedStart,
            plannedQty: slot!.plannedQuantity,
            timesheetEntries: timesheets,
            inventoryMovements: movements,
          }),
        )
      } catch {
        if (!cancelled) setCostBreakdown(null)
      }
    }
    void loadCost()
    return () => {
      cancelled = true
    }
  }, [slotId, slot])

  useEffect(() => {
    const productId = selectedLine?.product
    if (!productId || typeof productId !== 'string') {
      setCompatibleMachines([])
      return
    }
    let cancelled = false
    void loadCompatibleMachines(productId).then((list) => {
      if (!cancelled) setCompatibleMachines(list)
    })
    return () => {
      cancelled = true
    }
  }, [selectedLine?.product])

  useEffect(() => {
    if (isEdit) return
    if (machineOptions.length === 0) {
      if (machineId) setMachineId('')
      return
    }
    if (!machineOptions.some((m) => m.id === machineId)) {
      const preferred =
        initialMachineId && machineOptions.some((m) => m.id === initialMachineId)
          ? initialMachineId
          : machineOptions[0].id
      setMachineId(preferred)
    }
  }, [isEdit, machineOptions, machineId, initialMachineId])

  const resolvedProductOutput = resolveProductOutput(selectedProduct)
  const outputUnitMismatch =
    resolvedProductOutput &&
    selectedMachine &&
    !productOutputUnitsMatch(selectedProduct, selectedMachine)

  useEffect(() => {
    if (!isEdit) setScheduleMode('line')
  }, [requestLineId, machineId, isEdit])

  useEffect(() => {
    if (isEdit) return
    if (!selectedMachine || !selectedLine || !plannedStart) return
    if (outputUnitMismatch) return

    const startIso = localDateTimeToIso(plannedStart)

    if (scheduleMode === 'line') {
      const targetQty = lineRemaining > 0 ? lineRemaining : Number(selectedLine.quantity) || 0
      if (targetQty <= 0) return
      const hours = hoursForProductQuantity(selectedMachine, targetQty, selectedProduct)
      if (hours <= 0) return
      const endLocal = addHoursToLocalDateTime(plannedStart, hours)
      if (endLocal) setPlannedEnd(endLocal)
      setPlannedQuantity(String(Math.round(targetQty)))
      return
    }

    if (scheduleMode === 'duration' && plannedEnd) {
      const endIso = localDateTimeToIso(plannedEnd)
      if (new Date(endIso).getTime() <= new Date(startIso).getTime()) return
      const qty = calculatePlannedQuantity(selectedMachine, startIso, endIso, selectedProduct)
      setPlannedQuantity(String(qty))
      return
    }

    if (scheduleMode === 'quantity') {
      const qty = Math.max(0, Number(plannedQuantity) || 0)
      if (qty <= 0) return
      const hours = hoursForProductQuantity(selectedMachine, qty, selectedProduct)
      if (hours <= 0) return
      const endLocal = addHoursToLocalDateTime(plannedStart, hours)
      if (endLocal) setPlannedEnd(endLocal)
    }
  }, [
    isEdit,
    scheduleMode,
    selectedMachine,
    selectedLine,
    selectedProduct,
    plannedStart,
    plannedEnd,
    plannedQuantity,
    lineRemaining,
    outputUnitMismatch,
  ])

  const planHint =
    selectedMachine && plannedStart && plannedEnd && !outputUnitMismatch
      ? plannedQuantityHint(
          selectedMachine,
          localDateTimeToIso(plannedStart),
          localDateTimeToIso(plannedEnd),
          selectedProduct,
        )
      : null

  const endDurationHint =
    plannedStart && plannedEnd && !outputUnitMismatch
      ? (() => {
          const hours = slotDurationHours(localDateTimeToIso(plannedStart), localDateTimeToIso(plannedEnd))
          return hours > 0 ? `Длительность: ${formatDurationHours(hours)}` : null
        })()
      : null

  const lineScheduleHint =
    !isEdit && selectedLine && lineRemaining > 0 && scheduleMode === 'line' && selectedMachine && !outputUnitMismatch
      ? (() => {
          const hours = hoursForProductQuantity(selectedMachine, lineRemaining, selectedProduct)
          if (hours <= 0) return null
          return `По заявке: ${formatQty(lineRemaining)} ${productQuantityUnitLabel(selectedProduct)} → ≈ ${formatDurationHours(hours)}`
        })()
      : null

  useEffect(() => {
    if (!selectedLine?.product || !plannedStart) {
      setBomWarning(false)
      return
    }
    void pb
      .collection(COL_PRODUCT_COMPONENTS)
      .getFullList<ProductComponentRecord>({ filter: `product="${selectedLine.product}"` })
      .then((rows) => setBomWarning(hasBomChangedSinceSlot(rows, plannedStart)))
      .catch(() => setBomWarning(false))
  }, [selectedLine?.product, plannedStart, slot?.created])

  useEffect(() => {
    if (!machineId || !plannedStart || !plannedEnd) {
      setMachineEvents([])
      return
    }
    const startIso = localDateTimeToIso(plannedStart)
    const endIso = localDateTimeToIso(plannedEnd)
    void eventsForMachineInRange(machineId, startIso, endIso)
      .then(setMachineEvents)
      .catch(() => setMachineEvents([]))
  }, [machineId, plannedStart, plannedEnd, status])

  const handleStartDowntime = useCallback(async () => {
    if (!can(PERMISSION_KEYS.MACHINE_EVENTS_WRITE) || !machineId) return
    const durationMin = Math.max(1, Number(eventDurationMinutes) || 0)
    if (!Number.isFinite(durationMin) || durationMin <= 0) {
      setErr('Укажите длительность простоя больше нуля (минуты).')
      return
    }

    const startDate = new Date(eventStartLocal)
    if (!Number.isFinite(startDate.getTime())) {
      setErr('Укажите корректное время начала простоя.')
      return
    }

    setEventSaving(true)
    setErr('')
    setEventShiftMsg('')
    try {
      const startAt = startDate.toISOString()
      const endAt = new Date(startDate.getTime() + durationMin * 60_000).toISOString()

      await pb.collection(COL_MACHINE_EVENTS).create({
        machine: machineId,
        startAt,
        endAt,
        reason: eventReason,
        notes: eventNotes.trim(),
        recordedBy: user?.id || '',
      })

      let shifted = 0
      try {
        shifted = await applyDowntimeToSchedule(machineId, startAt, endAt)
      } catch (shiftErr: unknown) {
        setEventShiftMsg(
          pocketbaseErrorMessage(
            shiftErr,
            'Простой зарегистрирован, но не удалось сместить график — обратитесь к мастеру смены.',
          ),
        )
      }

      if (shifted > 0) {
        setEventShiftMsg(`График смещён: обновлено слотов — ${shifted}.`)
        const slotList = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
          sort: 'plannedStart',
        })
        setAllSlots(slotList)
        if (slotId) {
          const refreshed = await pb.collection(COL_SCHEDULE_SLOTS).getOne<ScheduleSlotRecord>(slotId)
          setSlot(refreshed)
          setPlannedStart(toLocalInput(refreshed.plannedStart))
          setPlannedEnd(toLocalInput(refreshed.plannedEnd))
        }
        onSaved()
      }

      setEventNotes('')
      setEventDurationMinutes('60')
      setEventStartLocal(formatLocalDateTimeInput(new Date()))
      const rangeStart = new Date(
        Math.min(startDate.getTime(), new Date(localDateTimeToIso(plannedStart)).getTime()),
      ).toISOString()
      const rangeEnd = new Date(
        Math.max(startDate.getTime() + durationMin * 60_000, new Date(localDateTimeToIso(plannedEnd)).getTime()),
      ).toISOString()
      setMachineEvents(await eventsForMachineInRange(machineId, rangeStart, rangeEnd))
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка регистрации простоя'))
    } finally {
      setEventSaving(false)
    }
  }, [
    can,
    machineId,
    eventReason,
    eventNotes,
    eventStartLocal,
    eventDurationMinutes,
    user?.id,
    plannedStart,
    plannedEnd,
    slotId,
    onSaved,
  ])

  const handleEndDowntime = useCallback(async (eventId: string) => {
    if (!can(PERMISSION_KEYS.MACHINE_EVENTS_WRITE)) return
    const ev = machineEvents.find((e) => e.id === eventId)
    if (!ev) return

    setEventSaving(true)
    setErr('')
    setEventShiftMsg('')
    try {
      const endAt = new Date().toISOString()
      await pb.collection(COL_MACHINE_EVENTS).update(eventId, { endAt })

      let shifted = 0
      if (machineId) {
        try {
          shifted = await applyDowntimeToSchedule(machineId, ev.startAt, endAt)
        } catch (shiftErr: unknown) {
          setEventShiftMsg(
            pocketbaseErrorMessage(shiftErr, 'Простой закрыт, но график не смещён — обратитесь к мастеру смены.'),
          )
        }
      }

      if (shifted > 0) {
        setEventShiftMsg(`График смещён: обновлено слотов — ${shifted}.`)
        if (slotId) {
          const refreshed = await pb.collection(COL_SCHEDULE_SLOTS).getOne<ScheduleSlotRecord>(slotId)
          setSlot(refreshed)
          setPlannedStart(toLocalInput(refreshed.plannedStart))
          setPlannedEnd(toLocalInput(refreshed.plannedEnd))
        }
        onSaved()
      }

      const startIso = localDateTimeToIso(plannedStart)
      const endIso = localDateTimeToIso(plannedEnd)
      setMachineEvents(await eventsForMachineInRange(machineId, startIso, endIso))
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка закрытия простоя'))
    } finally {
      setEventSaving(false)
    }
  }, [can, machineId, machineEvents, plannedStart, plannedEnd, slotId, onSaved])

  const handleSave = useCallback(async () => {
    if (!canWrite) return
    const overlap = warnings.find((w) => w.type === 'overlap' || w.type === 'plant_closed')
    if (overlap) {
      setErr(overlap.message)
      return
    }
    if (!requestLineId || !machineId || !plannedStart || !plannedEnd) {
      setErr('Заполните строку заявки, станок и интервал')
      return
    }

    setSaving(true)
    setErr('')
    const startIso = localDateTimeToIso(plannedStart)
    const endIso = localDateTimeToIso(plannedEnd)
    const machine = machines.find((m) => m.id === machineId)
    const qty =
      Number(plannedQuantity) ||
      (machine ? calculatePlannedQuantity(machine, startIso, endIso, selectedProduct) : 0)
    const prevStatus = slot?.status ?? null
    const productId = selectedLine?.product ?? null

    try {
      const payload: Record<string, unknown> = {
        requestLine: requestLineId,
        machine: machineId,
        plannedStart: startIso,
        plannedEnd: endIso,
        plannedQuantity: qty,
        outputUnit: resolveProductOutput(selectedProduct) ?? selectedProduct?.outputUnit ?? machine?.output_unit ?? 'sheets',
        status,
        notes: notes.trim(),
        shift: shiftId || '',
      }
      if (actualQuantity.trim() !== '') payload.actualQuantity = Math.max(0, Number(actualQuantity) || 0)
      if (status === 'completed' || defectQuantity.trim() !== '') {
        payload.defectQuantity = Math.max(0, Number(defectQuantity) || 0)
      }
      if (status === 'completed' && user?.id) payload.closedBy = user.id

      if (slotId) {
        const updated = await pb.collection(COL_SCHEDULE_SLOTS).update<ScheduleSlotRecord>(slotId, payload)
        await afterSlotSaved(updated, prevStatus, productId, user?.id)
        if (requestLineId) await syncRequestLineStatus(requestLineId)
        if (status === 'completed' && materialLotCode.trim()) {
          const lotErr = await linkMaterialLotToSlot(
            slotId,
            materialLotCode,
            materialLotQty.trim() !== '' ? Math.max(0, Number(materialLotQty) || 0) : undefined,
          )
          if (lotErr) {
            setErr(lotErr)
            onSaved()
            return
          }
        }
      } else {
        payload.createdBy = user?.id || ''
        const created = await pb.collection(COL_SCHEDULE_SLOTS).create<ScheduleSlotRecord>(payload)
        await afterSlotSaved(created, null, productId, user?.id)
        if (requestLineId) await syncRequestLineStatus(requestLineId)
        if (status === 'completed' && materialLotCode.trim()) {
          const lotErr = await linkMaterialLotToSlot(
            created.id,
            materialLotCode,
            materialLotQty.trim() !== '' ? Math.max(0, Number(materialLotQty) || 0) : undefined,
          )
          if (lotErr) {
            setErr(lotErr)
            onSaved()
            return
          }
        }
      }
      onSaved()
      onClose()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    } finally {
      setSaving(false)
    }
  }, [
    canWrite,
    warnings,
    requestLineId,
    machineId,
    plannedStart,
    plannedEnd,
    plannedQuantity,
    machines,
    status,
    notes,
    actualQuantity,
    defectQuantity,
    user?.id,
    slotId,
    shiftId,
    slot?.status,
    selectedLine?.product,
    selectedProduct,
    onSaved,
    onClose,
    materialLotCode,
    materialLotQty,
  ])

  const handleCreateRework = useCallback(async () => {
    if (!canWrite || !slot) return
    const defect = Math.max(0, Number(defectQuantity) || 0)
    if (defect <= 0) {
      setErr('Укажите количество брака для передела')
      return
    }
    if (!window.confirm(`Создать слот передела на ${defect} шт.?`)) return

    setReworkBusy(true)
    setErr('')
    try {
      await pb.collection(COL_SCHEDULE_SLOTS).create({
        requestLine: slot.requestLine,
        machine: slot.machine,
        plannedStart: slot.plannedEnd,
        plannedEnd: slot.plannedEnd,
        plannedQuantity: defect,
        outputUnit: slot.outputUnit,
        status: 'planned',
        shift: slot.shift || '',
        reworkOf: slot.id,
        notes: `Передел по слоту ${slot.id.slice(0, 8)}`,
        createdBy: user?.id || '',
      })
      onSaved()
      onClose()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка создания передела'))
    } finally {
      setReworkBusy(false)
    }
  }, [canWrite, slot, defectQuantity, user?.id, onSaved, onClose])

  const defectNum = Math.max(0, Number(defectQuantity) || 0)
  const showRework = canWrite && isEdit && (status === 'completed' || slot?.status === 'completed') && defectNum > 0 && !slot?.reworkOf

  return (
    <Modal title={isEdit ? 'Слот графика' : 'Новый слот'} onClose={onClose}>
      {loading ? (
        <p className="text-sm text-su-text">
          <em>Загрузка…</em>
        </p>
      ) : (
        <>
          {bomWarning && (
            <Alert kind="warn">
              BOM изменилась после постановки слота. Пересчитайте материалы и себестоимость перед закрытием.
            </Alert>
          )}
          {outputUnitMismatch && (
            <Alert kind="warn">
              Единица выпуска продукции ({resolvedProductOutput === 'rolls' ? 'рулоны' : 'листы'}) не совпадает
              с единицей станка ({selectedMachine?.output_unit === 'rolls' ? 'рулоны' : 'листы'}). План не
              рассчитается — выберите совместимый станок или измените единицу в карточке продукции.
            </Alert>
          )}
          {warnings.map((w) => (
            <Alert
              key={`${w.type}-${w.slotId ?? w.message}`}
              kind={w.type === 'overlap' || w.type === 'plant_closed' ? 'err' : 'warn'}
            >
              {w.message}
            </Alert>
          ))}
          {err && <Alert kind="err">{err}</Alert>}

          <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pr-1">
            {!isEdit && (
              <FormField label="Строка заявки" htmlFor="slot-line">
                <select
                  id="slot-line"
                  className={formInputProps().className}
                  value={requestLineId}
                  onChange={(e) => setRequestLineId(e.target.value)}
                  disabled={!canWrite || saving}
                >
                  {lines.map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.expand?.product?.name ?? line.id} · {line.quantity} шт.
                    </option>
                  ))}
                </select>
              </FormField>
            )}

            <FormField label="Станок" htmlFor="slot-machine">
              {machineOptions.length === 0 && selectedLine?.product && !isEdit ? (
                <div className="rounded-su border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                  У продукции не указаны совместимые станки. Настройте их в карточке продукции.
                </div>
              ) : (
                <select
                  id="slot-machine"
                  className={formInputProps().className}
                  value={machineId}
                  onChange={(e) => setMachineId(e.target.value)}
                  disabled={!canWrite || saving || isEdit || machineOptions.length === 0}
                >
                  {machineOptions.map((m) => (
                    <option key={m.id} value={m.id}>
                      {formatMachineLabel(m)}
                    </option>
                  ))}
                </select>
              )}
            </FormField>

            <FormField label="Смена" htmlFor="slot-shift">
              <select
                id="slot-shift"
                className={formInputProps().className}
                value={shiftId}
                onChange={(e) => setShiftId(e.target.value)}
                disabled={!canWrite || saving}
              >
                <option value="">—</option>
                {shifts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Начало" htmlFor="slot-start">
                <input
                  id="slot-start"
                  type="datetime-local"
                  value={plannedStart}
                  onChange={(e) => {
                    setScheduleMode('line')
                    setPlannedStart(e.target.value)
                  }}
                  disabled={!canWrite || saving}
                  {...formInputProps()}
                />
              </FormField>
              <FormField label="Окончание" htmlFor="slot-end">
                <input
                  id="slot-end"
                  type="datetime-local"
                  value={plannedEnd}
                  onChange={(e) => {
                    setScheduleMode('duration')
                    setPlannedEnd(e.target.value)
                  }}
                  disabled={!canWrite || saving}
                  {...formInputProps()}
                />
                {endDurationHint && <p className="mt-1 mb-0 text-xs text-su-text">{endDurationHint}</p>}
              </FormField>
            </div>
            {lineScheduleHint && <p className="-mt-1 mb-0 text-xs text-su-text">{lineScheduleHint}</p>}

            <FormField label={plannedQuantityFieldLabel(selectedProduct)} htmlFor="slot-planned">
              <input
                id="slot-planned"
                type="number"
                min={0}
                value={plannedQuantity}
                onChange={(e) => {
                  setScheduleMode('quantity')
                  setPlannedQuantity(e.target.value)
                }}
                disabled={!canWrite || saving}
                {...formInputProps()}
              />
              {planHint && <p className="mt-1 mb-0 text-xs text-su-text">{planHint}</p>}
            </FormField>

            <FormField label="Факт, шт." htmlFor="slot-actual">
              <input
                id="slot-actual"
                type="number"
                min={0}
                value={actualQuantity}
                onChange={(e) => setActualQuantity(e.target.value)}
                disabled={!canWrite || saving}
                {...formInputProps()}
              />
            </FormField>

            <FormField label="Брак, шт." htmlFor="slot-defect">
              <input
                id="slot-defect"
                type="number"
                min={0}
                value={defectQuantity}
                onChange={(e) => setDefectQuantity(e.target.value)}
                disabled={!canWrite || saving}
                {...formInputProps()}
              />
            </FormField>

            <FormField label="Статус" htmlFor="slot-status">
              <select
                id="slot-status"
                className={formInputProps().className}
                value={status}
                onChange={(e) => setStatus(e.target.value as ScheduleSlotRecord['status'])}
                disabled={!canWrite || saving}
              >
                {Object.entries(SCHEDULE_SLOT_STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Примечание" htmlFor="slot-notes">
              <textarea
                id="slot-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!canWrite || saving}
                className={formInputProps().className}
              />
            </FormField>

            {status === 'completed' && canWrite && (
              <div className="rounded-su border border-su-border bg-su-surface-muted p-3">
                <h4 className="mb-2 text-sm font-semibold text-su-text-strong">Партия сырья (прослеживаемость)</h4>
                <p className="mb-2 text-xs text-su-text">Необязательно: код партии из справочника material_lots.</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Код партии" htmlFor="slot-mat-lot">
                    <input
                      id="slot-mat-lot"
                      type="text"
                      value={materialLotCode}
                      onChange={(e) => setMaterialLotCode(e.target.value)}
                      disabled={saving}
                      placeholder="LOT-001"
                      {...formInputProps()}
                    />
                  </FormField>
                  <FormField label="Расход, ед." htmlFor="slot-mat-qty">
                    <input
                      id="slot-mat-qty"
                      type="number"
                      min={0}
                      value={materialLotQty}
                      onChange={(e) => setMaterialLotQty(e.target.value)}
                      disabled={saving}
                      placeholder="1"
                      {...formInputProps()}
                    />
                  </FormField>
                </div>
              </div>
            )}

            {isEdit && costBreakdown && (
              <div className="rounded-su border border-su-border bg-su-surface-muted p-3">
                <h4 className="mb-2 text-sm font-semibold text-su-text-strong">Себестоимость слота</h4>
                <div className="grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <p className="mb-1 font-medium text-su-text">План</p>
                    <ul className="m-0 list-none space-y-0.5 pl-0 text-su-text">
                      <li>Материалы: {formatMoney(costBreakdown.plan.materials)} ₽</li>
                      <li>Труд: {formatMoney(costBreakdown.plan.labor)} ₽</li>
                      <li className="font-semibold text-su-text-strong">Итого: {formatMoney(costBreakdown.plan.total)} ₽</li>
                    </ul>
                  </div>
                  <div>
                    <p className="mb-1 font-medium text-su-text">Факт</p>
                    <ul className="m-0 list-none space-y-0.5 pl-0 text-su-text">
                      <li>Материалы: {formatMoney(costBreakdown.actual.materials)} ₽</li>
                      <li>Труд: {formatMoney(costBreakdown.actual.labor)} ₽</li>
                      <li className="font-semibold text-su-text-strong">Итого: {formatMoney(costBreakdown.actual.total)} ₽</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {isEdit && slot && (
              <ScheduleOperationPanel
                slot={slot}
                product={selectedProduct}
                machines={machines}
                canWrite={canWrite}
                userId={user?.id}
                onDone={() => {
                  onSaved()
                  onClose()
                }}
                onError={setErr}
              />
            )}

            {(status === 'in_progress' || status === 'planned' || machineEvents.length > 0) && (
              <div className="rounded-su border border-su-border bg-su-surface-muted p-3">
                <h4 className="mb-2 text-sm font-semibold text-su-text-strong">Простои станка</h4>
                {machineEvents.length === 0 ? (
                  <p className="mb-2 text-xs text-su-text">Нет простоев за период слота.</p>
                ) : (
                  <ul className="mb-2 space-y-1 text-sm">
                    {machineEvents.map((ev) => (
                      <li key={ev.id} className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{machineEventReasonLabel(ev.reason)}</span>
                        <span className="text-su-text">{formatDateTimeRu(ev.startAt)}</span>
                        {ev.endAt ? (
                          <>
                            <span className="text-su-text">→ {formatDateTimeRu(ev.endAt)}</span>
                            <span className="text-xs text-su-text">
                              ({formatDurationHours(eventDurationHours(ev))})
                            </span>
                          </>
                        ) : (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">активен</span>
                        )}
                        {isEventOpen(ev) && can(PERMISSION_KEYS.MACHINE_EVENTS_WRITE) && (
                          <button
                            type="button"
                            className={modalBtn('ghost', 'sm')}
                            onClick={() => void handleEndDowntime(ev.id)}
                          >
                            Завершить
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {can(PERMISSION_KEYS.MACHINE_EVENTS_WRITE) &&
                  (status === 'in_progress' || status === 'planned') && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-end gap-2">
                      <FormField label="Начало" htmlFor="downtime-start">
                        <input
                          id="downtime-start"
                          type="datetime-local"
                          className={formInputProps().className}
                          value={eventStartLocal}
                          onChange={(e) => setEventStartLocal(e.target.value)}
                          disabled={eventSaving || saving}
                        />
                      </FormField>
                      <FormField label="Длительность, мин" htmlFor="downtime-duration">
                        <input
                          id="downtime-duration"
                          type="number"
                          min={1}
                          step={1}
                          className={formInputProps('w-28').className}
                          value={eventDurationMinutes}
                          onChange={(e) => setEventDurationMinutes(e.target.value)}
                          disabled={eventSaving || saving}
                        />
                      </FormField>
                      <select
                        className={formInputProps().className}
                        value={eventReason}
                        onChange={(e) => setEventReason(e.target.value as MachineEventReason)}
                        disabled={eventSaving || saving}
                      >
                        {MACHINE_EVENT_REASONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                      <input
                        className={formInputProps('min-w-32 flex-1').className}
                        placeholder="Комментарий"
                        value={eventNotes}
                        onChange={(e) => setEventNotes(e.target.value)}
                        disabled={eventSaving || saving}
                      />
                      <button
                        type="button"
                        className={modalBtn('primary', 'sm')}
                        disabled={eventSaving || saving}
                        onClick={() => void handleStartDowntime()}
                      >
                        {eventSaving ? 'Сохранение…' : 'Простой'}
                      </button>
                    </div>
                    <p className="m-0 text-xs text-su-text">
                      Последующие слоты на этом станке будут смещены на время простоя.
                    </p>
                  </div>
                )}
                {eventShiftMsg && (
                  <p className="mt-2 mb-0 text-xs text-emerald-700">{eventShiftMsg}</p>
                )}
              </div>
            )}
          </div>

          <ModalActions>
            {showRework && (
              <button
                type="button"
                className={modalBtn('primary')}
                onClick={() => void handleCreateRework()}
                disabled={reworkBusy || saving}
              >
                {reworkBusy ? 'Создание…' : 'Создать передел'}
              </button>
            )}
            <button type="button" className={modalBtn()} onClick={onClose} disabled={saving || reworkBusy}>
              Закрыть
            </button>
            {canWrite && (
              <button
                type="button"
                className={modalBtn('primary')}
                onClick={() => void handleSave()}
                disabled={saving || reworkBusy || loading}
              >
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            )}
          </ModalActions>
        </>
      )}
    </Modal>
  )
}
